// Lightweight mock API to allow the frontend to run without a backend.
// Exposes an axios-like interface (get/post) and named helpers used in the app.
const FAKE_DELAY = 300;

const byId = (arr, id) => arr.find((x) => x.id === id);

const wait = (ms) => new Promise((res) => setTimeout(res, ms));

// Seed data (kept in-memory; persisted to localStorage to survive refreshes)
const STORAGE_KEY = 'thinklink_mock_data_v1';
const loadState = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) return JSON.parse(raw);
  } catch (e) {}
  // default seed
  const now = Date.now();
  const seed = {
    users: [
      { id: 'u1', email: 'alice@example.com', password: 'password', name: 'Alice', points: 120, phoneVerified: true },
      { id: 'u2', email: 'bob@example.com', password: 'password', name: 'Bob', points: 45, phoneVerified: false }
    ],
    referrals: [
      { id: 'r1', referrerId: 'u1', referredUserId: 'u2', createdAt: now - 1000 * 60 * 60 * 24 * 10 }
    ],
    referralPoints: [
      { id: 'p1', userId: 'u1', points: 10, reason: 'first_referral', createdAt: now - 1000 * 60 * 60 * 24 * 9 }
    ]
  };
  return seed;
};

const saveState = (state) => localStorage.setItem(STORAGE_KEY, JSON.stringify(state));

let state = loadState();

const genId = (prefix = 'id') => `${prefix}_${Math.random().toString(36).slice(2, 9)}`;

// Basic analytics helpers
const weeklySeries = () => {
  // return last 7 days counts
  const oneDay = 24 * 60 * 60 * 1000;
  const now = Date.now();
  const series = [];
  for (let i = 6; i >= 0; i--) {
    const day = new Date(now - i * oneDay).toISOString().slice(0, 10);
    const count = state.referrals.filter(r => r.createdAt >= now - (i+1)*oneDay && r.createdAt < now - i*oneDay).length;
    series.push({ date: day, count });
  }
  return series;
};

const monthlySeries = () => {
  // last 6 months
  const now = new Date();
  const series = [];
  for (let i = 5; i >= 0; i--) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const month = `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}`;
    const count = state.referrals.filter(r => {
      const rD = new Date(r.createdAt);
      return rD.getFullYear() === d.getFullYear() && rD.getMonth() === d.getMonth();
    }).length;
    series.push({ month, count });
  }
  return series;
};

// Mock axios-like object
const mock = {
  get: async (url) => {
    await wait(FAKE_DELAY);
    if (url.startsWith('/referrals/activity')) {
      // parse query params page & limit
      const m = url.match(/page=(\d+).*limit=(\d+)/) || url.match(/page=(\d+)/);
      const page = m ? Number(m[1]) : 1;
      const limit = m && m[2] ? Number(m[2]) : 50;
      const start = (page - 1) * limit;
      const items = state.referralPoints.slice().reverse();
      const pageItems = items.slice(start, start + limit).map(p => ({ ...p }));
      return { data: { data: pageItems, page, limit, total: state.referralPoints.length } };
    }
    if (url === '/referrals/analytics') {
      return { data: { weeklySeries: weeklySeries(), monthlySeries: monthlySeries(), totalReferrals: state.referrals.length, totalPoints: state.referralPoints.reduce((s,p)=>s+p.points,0) } };
    }
    if (url === '/referrals') {
      // return referrals list
      const enriched = state.referrals.map(r => ({
        ...r,
        referrer: byId(state.users, r.referrerId) || null,
        referredUser: byId(state.users, r.referredUserId) || null
      }));
      return { data: enriched };
    }
    if (url === '/referrals/stats') {
      const totalPoints = state.referralPoints.reduce((s, p) => s + p.points, 0);
      return { data: { totalPoints, referralCount: state.referrals.length, statsByStatus: {} } };
    }
    // fallback: return empty
    return { data: {} };
  },

  post: async (url, payload) => {
    await wait(FAKE_DELAY);
    // Referrals
    if (url === '/referrals/link') {
      const user = getCurrentUser();
      if (!user) throw { response: { status: 401, data: { error: 'Unauthenticated' } } };
      const code = Buffer.from(String(user.id)).toString('base64');
      return { data: { referralLink: `${window.location.origin}/register?ref=${code}`, code } };
    }
    if (url === '/referrals/register') {
      // payload: { referralCode, userData }
      const { referralCode, userData } = payload;
      const newUser = { id: genId('u'), ...userData, points: 0, phoneVerified: false };
      state.users.push(newUser);
      // if referralCode present, decode and attach referral
      if (referralCode) {
        try {
          const referrerId = Buffer.from(referralCode, 'base64').toString('utf8');
          if (byId(state.users, referrerId)) {
            state.referrals.push({ id: genId('r'), referrerId, referredUserId: newUser.id, createdAt: Date.now() });
            const pointsRecord = { id: genId('p'), userId: referrerId, points: 10, reason: 'referral_signup', createdAt: Date.now() };
            state.referralPoints.push(pointsRecord);
            saveState(state);
          }
        } catch (e) {}
      }
      saveState(state);
      // issue fake token
      const token = `mock-token-${newUser.id}`;
      localStorage.setItem('token', token);
      localStorage.setItem('user', JSON.stringify(newUser));
      return { data: { token, user: newUser } };
    }

    // validate referral code
    if (url === '/referrals/validate') {
      const { referralCode } = payload;
      try {
        const referrerId = Buffer.from(referralCode, 'base64').toString('utf8');
        if (!byId(state.users, referrerId)) return { data: { valid: false } };
        return { data: { valid: true } };
      } catch (e) {
        return { data: { valid: false } };
      }
    }

    // Auth
    if (url === '/auth/login') {
      const { email, password } = payload;
      const user = state.users.find(u => u.email === email && u.password === password);
      if (!user) throw { response: { status: 401, data: { error: 'Invalid credentials' } } };
      const token = `mock-token-${user.id}`;
      localStorage.setItem('token', token);
      localStorage.setItem('user', JSON.stringify(user));
      return { data: { token, user } };
    }

    if (url === '/auth/register') {
      const userData = payload;
      const newUser = { id: genId('u'), ...userData, points: 0, phoneVerified: false };
      state.users.push(newUser);
      saveState(state);
      const token = `mock-token-${newUser.id}`;
      localStorage.setItem('token', token);
      localStorage.setItem('user', JSON.stringify(newUser));
      return { data: { token, user: newUser } };
    }

    if (url === '/auth/apply-referral') {
      const { userId, referralCode } = payload;
      try {
        const referrerId = Buffer.from(referralCode, 'base64').toString('utf8');
        if (!byId(state.users, referrerId)) return { data: { success: false, message: 'Invalid code' } };
        // prevent double applying
        if (state.referrals.find(r => r.referredUserId === userId)) {
          throw { response: { status: 409, data: { error: 'Already has referrer' } } };
        }
        state.referrals.push({ id: genId('r'), referrerId, referredUserId: userId, createdAt: Date.now() });
        const pointsRecord = { id: genId('p'), userId: referrerId, points: 5, reason: 'applied_referral', createdAt: Date.now() };
        state.referralPoints.push(pointsRecord);
        saveState(state);
        const user = byId(state.users, userId);
        return { data: { success: true, user } };
      } catch (e) {
        throw e;
      }
    }

    if (url === '/auth/verify-phone') {
      const { phone, code } = payload;
      // accept anything in mock
      const user = JSON.parse(localStorage.getItem('user') || 'null');
      if (user) {
        user.phoneVerified = true;
        localStorage.setItem('user', JSON.stringify(user));
      }
      return { data: { verified: true } };
    }

    if (url === '/auth/resend-verification') {
      return { data: { sent: true } };
    }

    if (url === '/referrals/award-points') {
      const { userId, points, reason } = payload;
      const rec = { id: genId('p'), userId, points, reason, createdAt: Date.now() };
      state.referralPoints.push(rec);
      saveState(state);
      return { data: { success: true, rec } };
    }

    // default
    return { data: {} };
  }
};

function getCurrentUser() {
  const u = localStorage.getItem('user');
  return u ? JSON.parse(u) : null;
}

// Named helpers used directly by some modules
const getReferrals = async () => {
  const res = await mock.get('/referrals');
  return res;
};

const getReferralStats = async () => {
  const res = await mock.get('/referrals/stats');
  return res;
};

const generateReferralLink = async () => {
  const res = await mock.post('/referrals/link');
  return res;
};

const registerWithReferral = async (referralCode, userData) => {
  const res = await mock.post('/referrals/register', { referralCode, userData });
  return res;
};

const awardReferralPoints = async (userId, points, reason) => {
  const res = await mock.post('/referrals/award-points', { userId, points, reason });
  return res;
};

export default mock;
export { getReferrals, getReferralStats, generateReferralLink, registerWithReferral, awardReferralPoints };
